import { CountryCode } from "libphonenumber-js";
import { FC, useEffect } from "react";
import { useI18n } from "../../hooks/useTranslations";
import { LeasingCompany } from "../../http/leasingCompanies/types";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import Input from "../ui/Input";
import PhoneNumberInput from "../ui/PhoneNumberInput";
import Textarea from "../ui/Textarea";

export interface LeasingCompanyFormBody {
  name: string;
  address?: string;
  email?: string;
  phoneNumber: string | null;
  countryPhone: CountryCode | null;
  notes?: string;
}

type LeasingCompanyFormProps = {
  leasingCompany?: LeasingCompany | null;
  onSubmit: (form: LeasingCompanyFormBody) => Promise<void>;
  isDisabled?: boolean;
  isLoading?: boolean;
};

const LeasingCompanyForm: FC<LeasingCompanyFormProps> = ({
  leasingCompany,
  onSubmit,
  isDisabled,
  isLoading,
}: LeasingCompanyFormProps) => {
  const i18n = useI18n();

  const defaultValues = {
    name: leasingCompany?.name,
    address: leasingCompany?.address,
    email: leasingCompany?.email,
    phoneNumber: leasingCompany?.phoneNumber,
    notes: leasingCompany?.notes,
  };

  return (
    <Form<LeasingCompanyFormBody>
      defaultValues={defaultValues}
      submitText={i18n.t.common.save}
      disabled={isDisabled}
      onSubmit={() => onSubmit}
      itemCreationInfo={leasingCompany}
    >
      {({ register, reset, formState: { errors }, setError, clearErrors, setValue }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [leasingCompany?.id]);

        return (
          <FormFieldsContainer>
            <FormField label={i18n.t.common.name} error={errors.name}>
              {({ labelId, isInvalid, isOptional }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isDisabled || isLoading}
                  {...register("name", {
                    required: !isOptional,
                    minLength: 1,
                  })}
                />
              )}
            </FormField>
            <FormField label={i18n.t.common.address} error={errors.address} isOptional>
              {({ labelId, isInvalid }) => (
                <Input id={labelId} isInvalid={isInvalid} disabled={isDisabled || isLoading} {...register("address")} />
              )}
            </FormField>
            <FormField label={i18n.t.common.email} error={errors.email} isOptional>
              {({ labelId, isInvalid }) => (
                <Input id={labelId} isInvalid={isInvalid} disabled={isDisabled || isLoading} {...register("email")} />
              )}
            </FormField>
            <FormField label={i18n.t.common.phoneNumber} error={errors.phoneNumber} isOptional>
              {({ labelId }) => {
                return (
                  <PhoneNumberInput
                    labelId={labelId}
                    isDisabled={isDisabled}
                    isLoading={isLoading}
                    onSetPhoneNumber={(v) => setValue("phoneNumber", v)}
                    onSetCountryPhone={(v) => setValue("countryPhone", v)}
                    phoneNumber={leasingCompany?.phoneNumber}
                    onError={(error) =>
                      error ? setError("phoneNumber", { message: error }) : clearErrors("phoneNumber")
                    }
                  />
                );
              }}
            </FormField>
            <FormField label={i18n.t.common.notes} error={errors.notes} isOptional fullWidth>
              {({ labelId, isOptional }) => (
                <Textarea
                  id={labelId}
                  disabled={isDisabled}
                  {...register("notes", {
                    required: !isOptional,
                    minLength: 1,
                  })}
                />
              )}
            </FormField>
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default LeasingCompanyForm;
